import { useCallback, useEffect, useState } from 'react';
import type { ThemeMode } from '@/charts/palette';

const STORAGE_KEY = 'stickyiq:theme';

type ThemePreference = ThemeMode | 'system';

function readPreference(): ThemePreference {
  if (typeof localStorage === 'undefined') return 'system';
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw === 'light' || raw === 'dark' ? raw : 'system';
}

function systemMode(): ThemeMode {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Aktivní režim + přepínač. Razítko `data-theme` na <html> musí přebít OS
 * nastavení v obou směrech — proto se při 'system' atribut odstraňuje úplně.
 */
export function useTheme() {
  const [preference, setPreference] = useState<ThemePreference>(readPreference);
  const [resolved, setResolved] = useState<ThemeMode>(() =>
    preference === 'system' ? systemMode() : preference,
  );

  useEffect(() => {
    const root = document.documentElement;
    if (preference === 'system') {
      root.removeAttribute('data-theme');
      setResolved(systemMode());
    } else {
      root.setAttribute('data-theme', preference);
      setResolved(preference);
    }
  }, [preference]);

  useEffect(() => {
    if (preference !== 'system' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setResolved(mq.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [preference]);

  const setTheme = useCallback((next: ThemePreference) => {
    if (next === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, next);
    setPreference(next);
  }, []);

  return { mode: resolved, preference, setTheme };
}
